import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage, useIntl } from 'react-intl';

import { GameplayTier } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { BrandXL } from '@sorare/core/src/atoms/typography';
import { Stars } from '@sorare/core/src/components/card/StarRank';

import { PulledCardStars } from '#src/components/essence/CardPullExperience/CardsReveal/PulledCardStars/index.tsx';
import { SplashText } from '#src/components/essence/CardPullExperience/CardsReveal/SplashText/index.tsx';

import type { BestStarRank_cardPull } from './__generated__/index.graphql.ts';

type Props = {
  cardPull: Nullable<BestStarRank_cardPull>;
};

export const BestStarRank = ({ cardPull }: Props) => {
  const { formatMessage } = useIntl();
  if (!cardPull) return null;
  const { bestQuality, bestGameplayTier } = cardPull;
  if (bestQuality === null && bestGameplayTier === null) return null;

  return (
    <SplashText>
      <BrandXL color="var(--c-onBase-success)" className="text-center">
        <FormattedMessage defaultMessage="Highest Rank Revealed:" />
      </BrandXL>
      {bestGameplayTier === GameplayTier.STAR ? (
        // The pull-level best is clamped to STAR to hide GOAT presence —
        // show "4 or 5 stars" so the user knows a GOAT is possible.
        <Horizontal
          gap={2}
          center
          aria-label={formatMessage({ defaultMessage: '4 or 5 stars' })}
          role="img"
        >
          <Stars count={4} size={4}>
            <span className="sr-only">
              <FormattedMessage defaultMessage="4 stars" />
            </span>
          </Stars>
          <BrandXL color="var(--c-onBase-success)" aria-hidden>
            <FormattedMessage defaultMessage="or" />
          </BrandXL>
          <Stars count={5} size={4} sparkles>
            <span className="sr-only">
              <FormattedMessage defaultMessage="5 stars" />
            </span>
          </Stars>
        </Horizontal>
      ) : (
        <PulledCardStars
          quality={bestQuality}
          gameplayTier={bestGameplayTier}
          size={4}
        />
      )}
    </SplashText>
  );
};

BestStarRank.fragments = {
  cardPull: gql`
    fragment BestStarRank_cardPull on CardPull {
      id
      bestQuality
      bestGameplayTier
    }
  ` as TypedDocumentNode<BestStarRank_cardPull>,
};
